/**
 * Tutor API Service
 * Handles all tutor-related API calls
 */

import { apiClient } from './api';

export const tutorService = {
  /**
   * Fetch all tutors with optional filters
   * @param {Object} filters - { search, subject, location, mode, min_price, max_price, page, page_size }
   * @returns {Promise<{count: number, total_pages: number, current_page: number, results: Array}>}
   */
  async fetchTutors(filters = {}) {
    return apiClient.get('/tutors/', filters);
  },

  /**
   * Fetch a single tutor by ID with detailed information
   * @param {number} id - Tutor ID
   * @returns {Promise<Object>}
   */
  async fetchTutorById(id, options = {}) {
    const params = {};
    if (options.preview) {
      params.preview = 'true';
    }
    return apiClient.get(`/tutors/${id}/`, params);
  },

  /**
   * Publish current user's tutor profile (set status to approved).
   */
  async publishMyTutor() {
    return apiClient.post('/tutors/me/publish/', {});
  },

  /**
   * Create a new tutor (submission for approval)
   * @param {FormData} formData - Form data with tutor information and photo
   * @returns {Promise<Object>}
   */
  async createTutor(formData) {
    // FormData is already prepared in the component
    return apiClient.postFormData('/tutors/', formData);
  },

  /**
   * Update an existing tutor
   * @param {number} id - Tutor ID
   * @param {Object} tutorData - Updated tutor information
   * @returns {Promise<Object>}
   */
  async updateTutor(id, tutorData) {
    return apiClient.put(`/tutors/${id}/`, tutorData);
  },

  /**
   * Delete (soft delete) a tutor
   * @param {number} id - Tutor ID
   * @returns {Promise<Object>}
   */
  async deleteTutor(id) {
    return apiClient.delete(`/tutors/${id}/`);
  },

  /**
   * Fetch the tutor profile for the current authenticated user
   * @returns {Promise<Object>}
   */
  async fetchMyTutor() {
    return apiClient.get('/tutors/me/');
  },

  /**
   * Record a public profile view (server dedupes by IP; browser via localStorage).
   */
  recordProfileView(id) {
    return apiClient.post(`/tutors/${id}/view/`, {}, { credentials: 'include' });
  },

  recordCallClick(id) {
    return apiClient.post(`/tutors/${id}/call-click/`, {}, { credentials: 'include' });
  },

  recordMessageClick(id, type) {
    return apiClient.post(
      `/tutors/${id}/message-click/`,
      { type },
      { credentials: 'include' }
    );
  },
};

export default tutorService;
